import axios from "axios";
import { useEffect, useRef, useState } from "react";
import Select, { MultiValue } from "react-select";
import { Controller, Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { toast } from "react-hot-toast";
import { ScaleLoader } from "react-spinners";
import _lodash from "lodash";
import { InputErrorMessage } from "../input/InputErrorMessage";
import { CustomLabel } from "../input/CustomLabel";
import { possuiMensagemDeErro } from "shared/util/functions/errors/RequestPossuiMensagemDeErro";
import { getMensagemDeErroBackend } from "shared/util/functions/errors/GetMensagemDeErroBackend";

interface MultiSelectAsyncProps {
  id: string;
  nomeAtributoValue: string;
  nomeAtributoLabel: string;
  placeholder?: string;
  endpoint: string;
  invalidateQuerysKey?: any[];
  className?: string;
  label?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  atributosRefresh?: any[];
  possuiErro?: boolean;
  required?: boolean;
  onChange?: (opcaoSelecionada: MultiValue<any>) => void;
  defaultValue?: MultiValue<any>;
  transformResponse?: (data: any) => any;
  usarValidacaoParaFormulario: boolean;
  name?: string;
  register?: UseFormRegister<any>;
  control?: Control<any>;
  setValue?: UseFormSetValue<any>;
}

const MultiSelectAsync = ({
  id,
  nomeAtributoValue,
  nomeAtributoLabel,
  placeholder,
  endpoint,
  className,
  label,
  isDisabled,
  isClearable,
  atributosRefresh,
  required,
  onChange,
  defaultValue,
  transformResponse,
  usarValidacaoParaFormulario,
  name,
  register,
  control,
  setValue,
}: MultiSelectAsyncProps) => {
  const prevAtributosRefresh = useRef<any[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deveBuscarOpcoes, setDeveBuscarOpcoes] = useState(true);

  useEffect(() => {
    if (!_lodash.isEqual(prevAtributosRefresh.current, atributosRefresh)) {
      setDeveBuscarOpcoes(true);
      prevAtributosRefresh.current = atributosRefresh || [];
    }
  }, [atributosRefresh]);

  const fetchOptions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(endpoint);
      let registros = response.data?.content || response.data?.payload || response.data || [];
      if (transformResponse) {
        registros = transformResponse(registros);
      }
      setOptions(
        registros.map((registro: any) => ({
          value: registro[nomeAtributoValue],
          label: registro[nomeAtributoLabel],
        }))
      );
    } catch (error) {
      const errorMessage = possuiMensagemDeErro(error) ? getMensagemDeErroBackend(error) : "Erro ao buscar opções";
      toast.error(errorMessage);
      console.error("Fetch Options Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuOpen = () => {
    if (deveBuscarOpcoes) {
      setOptions([]);
      fetchOptions();
      setDeveBuscarOpcoes(false);
    }
  };

  const renderLoadingMessage = () => (
    <div>
      <strong>Carregando opções</strong>
      <ScaleLoader height="10px" color="#83cf9c" />
    </div>
  );

  const selectProps = {
    id,
    styles: {
      control: (provided: any, state: any) => ({
        ...provided,
        borderColor: state.isFocused ? "#83cf9c" : "#c4c4c4",
        boxShadow: "none",
        fontSize: "small",
        fontWeight: "200",
      }),
    },
    isMulti: true,
    isClearable,
    isDisabled,
    className: className || "",
    placeholder: placeholder || "",
    onMenuOpen: handleMenuOpen,
    isLoading,
    loadingMessage: renderLoadingMessage,
    noOptionsMessage: () => "Nenhum registro foi encontrado",
    options,
  };

  const handleChange = (newValue: MultiValue<any>) => {
    if (usarValidacaoParaFormulario && name && control && setValue) {
      setValue(name, newValue);
    }
    onChange?.(newValue);
  };

  const renderSelect = (field?: any, fieldState?: any) => (
    <>
      {label && <CustomLabel possuiErro={!!fieldState?.error}>{required ? `${label} *` : label}</CustomLabel>}
      <Select
        {...(register ? register(name || "") : {})}
        {...(field || {})}
        {...selectProps}
        value={field ? field.value || [] : defaultValue || []}
        onChange={handleChange}
      />
      {fieldState?.error && <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>}
    </>
  );

  return (
    <Controller
      name={name || ""}
      control={control}
      defaultValue={defaultValue || []}
      render={({ field, fieldState }) => renderSelect(field, fieldState)}
    />
  );
};

export default MultiSelectAsync;
